import { useVideoPlayer, VideoView } from "expo-video";
import { useState } from "react";
import { Image, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import { BoldText } from "../BoldText";
import { CourseIcon } from "../svg/CourseIcon";

import { ScreenHeader } from "./ScreenHeader";

import { courseTheme } from "@/lib/training/courseTheme";
import type { Screen } from "@/lib/training/types";

type Video = Extract<Screen, { kind: "video" }>;

// Module intro + video screen. The clip STREAMS from the training media CDN —
// `screen.videoUrl` comes from the bundle content the API serves, so nothing is
// compiled into the app binary. Before first play we show the poster frame +
// play affordance to match the source design.
// DIVERGENCE: the source's custom scrubber + backdrop-blur chip are approximated;
// once playing we hand off to the platform video controls.

export function VideoScreen({ screen }: { screen: Video }) {
    const player = useVideoPlayer(screen.videoUrl, (p) => {
        p.loop = false;
    });
    const [started, setStarted] = useState(false);

    function play() {
        player.play();
        setStarted(true);
    }

    return (
        <View>
            <ScreenHeader
                kicker={screen.kicker ?? ""}
                title={screen.title}
                lede={screen.lede ?? ""}
            />

            <View style={styles.video}>
                <View
                    style={[
                        styles.poster,
                        screen.videoAspect === "9:16"
                            ? { aspectRatio: 9 / 16, width: "64%" }
                            : { aspectRatio: 16 / 9, width: "100%" }
                    ]}
                >
                    {/* Real frame thumbnail sits behind the video as the poster. */}
                    <Image
                        source={{ uri: screen.posterUrl }}
                        style={StyleSheet.absoluteFill}
                        resizeMode="cover"
                    />
                    <VideoView
                        player={player}
                        style={styles.videoFill}
                        contentFit="cover"
                        nativeControls={started}
                    />
                    {!started ? (
                        <View style={StyleSheet.absoluteFill}>
                            <View style={styles.vlabel}>
                                <Text style={styles.vlabelText}>{screen.vlabel}</Text>
                            </View>
                            <View style={styles.vdur}>
                                <Text style={styles.vdurText}>{screen.durationLabel}</Text>
                            </View>
                            <TouchableOpacity
                                style={styles.vplay}
                                activeOpacity={0.85}
                                onPress={play}
                            >
                                <View style={styles.vplayBtn}>
                                    <CourseIcon id="play" size={26} color={courseTheme.navy} />
                                </View>
                            </TouchableOpacity>
                        </View>
                    ) : null}
                </View>
            </View>

            {screen.caption ? (
                <BoldText
                    text={screen.caption}
                    style={styles.caption}
                    boldColor={courseTheme.navy}
                />
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    video: { marginTop: 18, alignItems: "center" },
    poster: {
        position: "relative",
        borderRadius: 20,
        overflow: "hidden",
        backgroundColor: courseTheme.navy
    },
    // `absoluteFill` alone does NOT size the video on web. VideoView renders a
    // real <video>, and CSS treats a replaced element with `width: auto` as
    // keeping its intrinsic size, so `right`/`bottom` are dropped as
    // over-constrained: a 1080x1920 clip laid out at 1080x1920 inside a ~224x398
    // box, clipped to a magnified top-left crop of the frame. The explicit
    // 100%/100% stretches it so `contentFit: cover` has a box to cover. On
    // native this is the same fill absoluteFill already produced.
    videoFill: { ...StyleSheet.absoluteFill, width: "100%", height: "100%" },
    vlabel: {
        position: "absolute",
        top: 11,
        left: 11,
        backgroundColor: "rgba(11,45,77,0.7)",
        borderRadius: 7,
        paddingHorizontal: 9,
        paddingVertical: 4
    },
    vlabelText: { fontSize: 11, fontWeight: "700", color: "#FFFFFF" },
    vdur: {
        position: "absolute",
        bottom: 11,
        right: 11,
        backgroundColor: "rgba(11,45,77,0.7)",
        borderRadius: 7,
        paddingHorizontal: 9,
        paddingVertical: 4
    },
    vdurText: { fontSize: 11, fontWeight: "700", color: "#FFFFFF" },
    vplay: { ...StyleSheet.absoluteFill, alignItems: "center", justifyContent: "center" },
    vplayBtn: {
        width: 62,
        height: 62,
        borderRadius: 31,
        backgroundColor: "rgba(255,255,255,0.95)",
        alignItems: "center",
        justifyContent: "center"
    },
    vscrub: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: 3,
        backgroundColor: "rgba(255,255,255,0.2)"
    },
    vscrubBar: { width: "0%", height: "100%", backgroundColor: courseTheme.tealL },
    caption: { fontSize: 12.5, lineHeight: 18, color: courseTheme.slate, marginTop: 12 }
});
